import { useState } from 'react';
import { X } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsTrigger } from '@/components/ui/tabs';
import { ScrollableTabsList } from '@/components/ScrollableTabsList';
import Loader from '@/components/Loader';
import { useHIWorkOrderByNumber, useHIInvoice } from '@/services/homeInspectionService';
import { useHIAttempts, useHIAttemptEmailLogs } from '@/services/homeInspectionAttemptService';
import { HIWorkOrderSidePanelHeader } from './HIWorkOrderSidePanelHeader';
import { HIWorkOrderSidePanelClientInfo } from './HIWorkOrderSidePanelClientInfo';
import { HIWorkOrderSidePanelServiceDetails } from './HIWorkOrderSidePanelServiceDetails';
import { HIWorkOrderSidePanelBilling } from './HIWorkOrderSidePanelBilling';
import { HIWorkOrderSidePanelAttempts } from './HIWorkOrderSidePanelAttempts';
import { ChangeLogSection } from '@/components/ChangeLogSection';
import { useUserStore } from '@/stores/user';

type Props = {
  workOrderNumber: string;
  onClose: () => void;
};

const tabs = [
  { value: 'CLIENT_INFO',      label: 'Client Info' },
  { value: 'SERVICE_DETAILS',  label: 'Service Details' },
  { value: 'BILLING',          label: 'Billing' },
  { value: 'STATUS_UPDATES',   label: 'Status Updates' },
];

const CHANGE_LOG_TAB = { value: 'CHANGE_LOG', label: 'Change Log' };

const HIWorkOrderSidePanel = ({ workOrderNumber, onClose }: Props) => {
  const { data: workOrder, isFetching, isError, refetch } = useHIWorkOrderByNumber(workOrderNumber);
  const workOrderId = workOrder?.workOrderId ?? null;
  const { data: invoice } = useHIInvoice(workOrderId);
  const { data: attempts } = useHIAttempts(workOrderId);
  const { data: attemptEmailLogs } = useHIAttemptEmailLogs(workOrderId);
  const [activeTab, setActiveTab] = useState(tabs[0].value);
  const isServer = useUserStore((state) => state.user?.roleId === 'SERVER');
  const visibleTabs = isServer ? tabs : [...tabs, CHANGE_LOG_TAB];

  return (
    <Sheet modal={false} open={true}>
      <SheetContent
        hideCloseButton={true}
        className="w-[80vw] md:w-[500px] scrollbar-hide bg-white"
        side="right"
        tabIndex={-1}
      >
        <Loader isLoading={isFetching}>
          {isError ? (
            <div className="flex h-full items-center relative justify-center w-full text-placeholder flex-col gap-4">
              <button
                type="button"
                aria-label="Close side panel"
                className="border-[1px] p-[6px] absolute top-6 right-6 rounded-md cursor-pointer"
                onClick={onClose}
              >
                <X color="#676767" className="h-[20px] w-[20px]" />
              </button>
              <span>FAILED TO LOAD WORK ORDER DETAILS</span>
              <button
                className="text-primary text-sm underline"
                onClick={() => refetch()}
              >
                Retry
              </button>
            </div>
          ) : workOrder ? (
            <Tabs className="w-full text-black" value={activeTab} onValueChange={setActiveTab}>
              <SheetHeader className="flex flex-col gap-4 pb-0 sticky top-0 bg-white">
                <SheetTitle className="flex flex-col gap-4 bg-white pb-0">
                  <HIWorkOrderSidePanelHeader
                    workOrder={workOrder}
                    attempts={attempts ?? []}
                    invoice={invoice}
                    onClose={onClose}
                  />
                </SheetTitle>
                <ScrollableTabsList className="!mt-0">
                  {visibleTabs.map((tab) => (
                    <TabsTrigger
                      key={tab.value}
                      value={tab.value}
                      className="w-1/3 flex-none"
                    >
                      {tab.label}
                    </TabsTrigger>
                  ))}
                </ScrollableTabsList>
              </SheetHeader>

              <div className="w-full !mt-0 pb-2 text-[#0A0A0A]">
                <TabsContent value="CLIENT_INFO" className="mt-0 h-full overflow-y-scroll scrollbar-hide">
                  <HIWorkOrderSidePanelClientInfo workOrder={workOrder} />
                </TabsContent>
                <TabsContent value="SERVICE_DETAILS" className="mt-0 h-full overflow-y-scroll scrollbar-hide">
                  <HIWorkOrderSidePanelServiceDetails workOrder={workOrder} />
                </TabsContent>
                <TabsContent value="BILLING" className="mt-0 h-full overflow-y-scroll scrollbar-hide">
                  <HIWorkOrderSidePanelBilling
                    invoice={invoice}
                    workOrderId={workOrder.workOrderId}
                    workOrderNumber={workOrder.workOrderNumber}
                  />
                </TabsContent>
                <TabsContent value="STATUS_UPDATES" className="mt-0 h-full overflow-y-scroll scrollbar-hide">
                  <HIWorkOrderSidePanelAttempts
                    workOrderNumber={workOrder.workOrderNumber}
                    attempts={attempts ?? []}
                    emailLogs={attemptEmailLogs ?? []}
                  />
                </TabsContent>
                {!isServer && (
                  <TabsContent value="CHANGE_LOG" className="mt-0 h-full overflow-y-scroll scrollbar-hide">
                    <ChangeLogSection
                      target={{ recordType: 'WORK_ORDER', serviceType: 'HOUSE_INSPECTION', recordId: workOrder.workOrderId }}
                      active={activeTab === 'CHANGE_LOG'}
                      className="px-6"
                    />
                  </TabsContent>
                )}
              </div>
            </Tabs>
          ) : null}
        </Loader>
      </SheetContent>
    </Sheet>
  );
};

export default HIWorkOrderSidePanel;
